import React, { useEffect, useRef, useState } from 'react';
import { tocarPresenteTransparente } from './videoTransparente';
import { urlRapido } from './presentesCache';

// PRESENTE EM VÍDEO SEM FUNDO
//
// Recebe um MP4 "empilhado" (cores em cima, máscara em baixo — ver
// videoTransparente.js) e desenha só o presente, com o que estiver por trás
// (a câmara da live) visível à volta. Funciona igual no iPhone e no Android.
//
// `aoTerminar` é chamado UMA vez: quando o vídeo acaba, se der erro, ou se o
// tempo limite passar. O ecrã que o usa só tem de limpar o seu estado aí.
// `aoComecar` (opcional) é chamado uma vez quando o presente entra — a Arena
// usa-o para o som da bomba, que antes saía do próprio <video>.

export default function PresenteTransparente({ src, aoTerminar, aoComecar, tamanho = 300, comSom = true }) {
  const refVideo = useRef(null);
  const refCanvas = useRef(null);
  // Guardado numa ref para o efeito não recomeçar o vídeo cada vez que o pai
  // volta a desenhar e passa uma função nova.
  const refAoTerminar = useRef(aoTerminar);
  const refAoComecar = useRef(aoComecar);
  // O React em desenvolvimento corre o efeito duas vezes: o som só sai uma.
  const refJaComecou = useRef(null);
  const [semWebGL, setSemWebGL] = useState(false);
  // O url que vai mesmo para o <video>: um blob: da cache se o presente já
  // foi descarregado (toca de imediato), senão o próprio src (toca da rede).
  // Guarda-se o par {src, url}: se o src mudar, o url antigo deixa de contar
  // sem ser preciso limpar o estado dentro do efeito.
  const [resolvido, setResolvido] = useState(null);
  const srcReal = resolvido && resolvido.src === src ? resolvido.url : null;

  useEffect(() => { refAoTerminar.current = aoTerminar; }, [aoTerminar]);
  useEffect(() => { refAoComecar.current = aoComecar; }, [aoComecar]);

  useEffect(() => {
    let vivo = true;
    if (!src) return undefined;
    urlRapido(src).then((url) => { if (vivo) setResolvido({ src, url: url || src }); });
    return () => { vivo = false; };
  }, [src]);

  useEffect(() => {
    const video = refVideo.current;
    const canvas = refCanvas.current;
    if (!video || !canvas || !srcReal) return undefined;

    const sessao = tocarPresenteTransparente({
      video,
      canvas,
      comSom,
      aoTerminar: (motivo) => {
        if (motivo !== 'fim') console.warn('[presente] terminou por:', motivo);
        refAoTerminar.current?.(motivo);
      },
    });
    if (!sessao.temWebGL) setSemWebGL(true);
    if (refJaComecou.current !== src) {
      refJaComecou.current = src;
      try { refAoComecar.current?.(); } catch (erro) { console.warn('[presente] aoComecar falhou:', erro); }
    }

    return () => sessao.parar();
  }, [src, srcReal, comSom]);

  return (
    <div style={{ position: 'relative', width: `${tamanho}px`, height: `${tamanho}px`, pointerEvents: 'none', overflow: 'hidden' }}>
      {/* O vídeo tem de estar no ecrã para o iPhone o descodificar — fica
          minúsculo e invisível. Com `display: none` o Safari pára de o tocar. */}
      <video
        ref={refVideo}
        src={srcReal || undefined}
        playsInline
        preload="auto"
        disablePictureInPicture
        style={semWebGL
          // Sem WebGL (raríssimo): o vídeo é 1:2, por isso com a largura toda
          // só a metade de cima (as cores) cabe no quadrado. Fica com fundo.
          ? { width: '100%', display: 'block' }
          : { position: 'absolute', width: '1px', height: '1px', opacity: 0, left: 0, top: 0 }}
      />
      <canvas
        ref={refCanvas}
        style={{ display: semWebGL ? 'none' : 'block', width: '100%', height: '100%', backgroundColor: 'transparent' }}
      />
    </div>
  );
}
